import React, { useState, useEffect, useMemo, useRef } from 'react'
import { CheckSquare, Square, Info } from '@phosphor-icons/react'

// ─── Calculadora BREEAM ES MAT 1 oficial ─────────────────────────────────────
// Replica la estructura del Excel oficial ITG (hoja "OneClickLCA").
// Mide RIGOR del ACV en 6 dimensiones de calidad + 1 dimensión de alcance.
// Conforme a Tabla 33 del manual BREEAM ES NC 2015 NA3: NO mide GWP absoluto.
//
// Total puntos máximos calculadora = 70 (suma de las 6 dimensiones de calidad)
// + alcance de materiales (max ~22 pts variables según proyecto).
// El % final se aplica a la Tabla 33 para determinar puntos BREEAM:
//   ≥10%   → 1 pt (industrial) / 1 pt (resto)
//   ≥35%   → 1 / 2
//   ≥62,5% → 1 / 3
//   ≥75%   → 2 / 4
//   ≥80%   → 2 / 5
//   ≥82,5% → 2 / 6
//   ≥85%   → 2 + ejemplar / 6 + ejemplar (+1 pt INO 1)

const C = {
  brown: '#2C2017', dark: '#3B2F2F', cream: '#F5F0EA', card: '#FFFFFF',
  border: '#DDD5C8', muted: '#5C4A3A', accent: '#7A5FA0',
  olive: '#7A6A5A', green: '#3F7858', red: '#8B3A3A', amber: '#C68B3F',
}
const CARD = { background: C.card, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '1rem 1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)' }
const ROW_BTN = { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', color: C.dark, transition: 'background 0.12s' }

const DIMENSIONS = [
  {
    id: 'indicadoresSalida',
    label: '1. Indicadores de salida disponibles',
    description: 'Indicadores ambientales incluidos en el ACV (output del análisis)',
    weight: 2,
    options: [
      { id: 'carbonoIncorporado', label: 'Carbono incorporado (CO₂e)', score: 1, mandatory: true },
      { id: 'aguaIncorporada', label: 'Agua incorporada O tratamiento de aguas residuales', score: 1 },
      { id: 'dosIndicadoresAdicionales', label: 'Y dos indicadores adicionales (ODP, AP, EP, etc.)', score: 2 },
    ],
    multi: true,
    maxPts: 8,
  },
  {
    id: 'etapasCV',
    label: '2. Etapas disponibles del ciclo de vida',
    description: 'Alcance del ACV en términos de etapas EN 15978',
    weight: 2,
    options: [
      { id: 'cunaPuerta', label: 'Cuna a Puerta total (A1-A3)', score: 1 },
      { id: 'cunaPuertaFinVida', label: 'Cuna a Puerta + Fin de Vida (A1-A3 + C)', score: 2 },
      { id: 'cunaTumba', label: 'Cuna a Tumba total (A1-C4 + D)', score: 3 },
      { id: 'cunaTumbaEnergia', label: 'Cuna a Tumba CON energía operacional (B6)', score: 4 },
      { id: 'cunaTumbaSeparadas', label: 'Cuna a Tumba indicando de forma separada a/b/c/d', score: 6 },
    ],
    radio: true, // solo una opción - la mejor
    maxPts: 12,
  },
  {
    id: 'niveles',
    label: '3. Niveles de evaluación',
    description: 'Granularidad del análisis (elemento, edificio, benchmark)',
    weight: 2,
    options: [
      { id: 'nivelElemental', label: 'Nivel elemental (cada elemento individual)', score: 2 },
      { id: 'nivelEdificio', label: 'Nivel de todo el edificio', score: 2 },
      { id: 'nivelEdificioBenchmark', label: 'Nivel de todo el edificio empleando un benchmark de referencia', score: 1 },
    ],
    multi: true,
    maxPts: 10,
  },
  {
    id: 'calidadOrigen',
    label: '4. Calidad de los datos del ACV — Origen',
    description: 'Procedencia y antigüedad de los datos del ACV',
    weight: 2,
    options: [
      { id: 'origenFabricante', label: 'Datos de ACV de origen específico del fabricante (DAPs)', score: 0 },
      { id: 'origenSector', label: 'Datos de ACV promedio del sector (genéricos validados)', score: 2.5 },
      { id: 'origenRecientes', label: 'Datos no mayores de 10 años', score: 2.5 },
    ],
    multi: true,
    maxPts: 10,
  },
  {
    id: 'calidadMetodo',
    label: '5. Calidad de los datos del ACV — Método de impactos',
    description: 'Método de evaluación de impactos según norma de referencia',
    weight: 4,
    options: [
      { id: 'metodoPropio', label: 'Método propio (no público)', score: 0 },
      { id: 'metodoPublico', label: 'Método disponible públicamente sin RCP', score: 1 },
      { id: 'metodoPublicoRCP', label: 'Método disponible públicamente CON RCP (Reglas Categoría Productos)', score: 2 },
      { id: 'pas2050', label: 'PAS 2050', score: 3 },
      { id: 'iso21930', label: 'ISO 21930', score: 4 },
      { id: 'en15804', label: 'EN 15804', score: 5 },
    ],
    radio: true,
    maxPts: 20,
  },
  {
    id: 'calidadVerificacion',
    label: '6. Calidad de los datos del ACV — Verificación',
    description: 'Verificación independiente de los datos del ACV',
    weight: 1, // los scores ya están en escala 0-5 cada uno → 5+5=10 = maxPts
    options: [
      { id: 'noVerificados', label: 'Datos ACV no verificados / desconocidos', score: 0 },
      { id: 'verificados', label: 'Datos ACV Verificados', score: 5 },
      { id: 'verificadosICV', label: 'Y la mayoría del ICV (Inventario Ciclo Vida) verificada', score: 5 },
    ],
    multi: true,
    maxPts: 10,
  },
]

// Alcance de materiales — escala separada, contribuye al % final
const ALCANCE_MATERIALES = [
  { id: 'fachadas', label: 'Fachadas (envolvente, estructura)', score: 2, mandatory: true },
  { id: 'ventanasLucernarios', label: 'Ventanas y lucernarios exteriores', score: 2, mandatory: true },
  { id: 'cimientos', label: 'Cimientos (incluida la excavación)', score: 2, mandatory: true },
  { id: 'pavimentosInteriores', label: 'Pavimentos interiores (parquet, vinilo, etc.)', score: 2 },
  { id: 'estructuraVertical', label: 'Estructura portante (vertical)', score: 2, mandatory: true },
  { id: 'cimentacionMuros', label: 'Cimentación / muros de contención', score: 1 },
  { id: 'dispositivosSolares', label: 'Dispositivos de protección solar', score: 1 },
  { id: 'losasForjados', label: 'Losas o forjados de planta baja', score: 1 },
  { id: 'techos', label: 'Techos (incl. suspendidos / acabados)', score: 1 },
  { id: 'particionesInteriores', label: 'Particiones interiores verticales', score: 1, mandatory: true },
  { id: 'cubiertas', label: 'Cubiertas (incluida cubrición)', score: 1, mandatory: true },
  { id: 'escalerasRampas', label: 'Escaleras y rampas', score: 1 },
  { id: 'balaustradasPasamanos', label: 'Balaustradas y pasamanos', score: 0.5 },
  { id: 'puertasInteriores', label: 'Puertas interiores', score: 0.5 },
  { id: 'paramentosInteriores', label: 'Paramentos interiores (acabados)', score: 0.5 },
  { id: 'instalacionesCalor', label: 'Fuente de calor, Calefacción, ACS', score: 1 },
  { id: 'instalacionesElectricas', label: 'Instalaciones eléctricas', score: 1 },
  { id: 'instalacionesPCI', label: 'Protección contra incendios y seguridad', score: 1 },
  { id: 'instalacionesComunicacion', label: 'Comunicación, seguridad y sistemas', score: 1 },
  { id: 'paisajeAjardinadoDuro', label: 'Paisaje ajardinado duro (carreteras, aparcamiento)', score: 1 },
  { id: 'paisajeAjardinadoCerramiento', label: 'Paisaje ajardinado duro (cierres exteriores)', score: 0.5 },
]
const ALCANCE_MAX = ALCANCE_MATERIALES.reduce((s, o) => s + o.score, 0)

// Total puntos máximos calidad (6 dimensiones)
const TOTAL_CALIDAD_MAX = DIMENSIONS.reduce((s, d) => s + d.maxPts, 0) // = 70

// Tabla 33 — % calculadora → puntos BREEAM
function pctToPoints(pct, isIndustrial) {
  if (pct >= 85.0) return { base: isIndustrial ? 2 : 6, ejemplar: 1 }
  if (pct >= 82.5) return { base: isIndustrial ? 2 : 6, ejemplar: 0 }
  if (pct >= 80.0) return { base: isIndustrial ? 2 : 5, ejemplar: 0 }
  if (pct >= 75.0) return { base: isIndustrial ? 2 : 4, ejemplar: 0 }
  if (pct >= 62.5) return { base: 1, ejemplar: 0 }
  if (pct >= 35.0) return { base: isIndustrial ? 1 : 2, ejemplar: 0 }
  if (pct >= 10.0) return { base: 1, ejemplar: 0 }
  return { base: 0, ejemplar: 0 }
}

export default function MAT1OfficialCalculator({ initialState, onChange }) {
  // Estado: selecciones por dimensión
  const [calidad, setCalidad] = useState(() => initialState?.calidad || {})
  const [alcance, setAlcance] = useState(() => initialState?.alcance || {})
  const [isIndustrial, setIsIndustrial] = useState(() => initialState?.isIndustrial || false)

  // Sync cuando initialState cambia (por ejemplo tras cargar de Supabase o tras
  // procesar un ACV OneClick). El lazy init de useState solo corre la primera
  // vez — sin este effect, los datos remotos se ignoran silenciosamente.
  // Comparamos por JSON.stringify para evitar bucles cuando el padre pasa una
  // nueva referencia con el mismo contenido tras el onChange.
  const lastInitKeyRef = useRef(null)
  useEffect(() => {
    if (!initialState) return
    const key = JSON.stringify({
      c: initialState.calidad || null,
      a: initialState.alcance || null,
      i: initialState.isIndustrial || false,
    })
    if (key === lastInitKeyRef.current) return
    lastInitKeyRef.current = key
    setCalidad(initialState.calidad || {})
    setAlcance(initialState.alcance || {})
    setIsIndustrial(initialState.isIndustrial || false)
  }, [initialState])

  // Toggle helpers
  const toggleMulti = (dimId, optId) => {
    setCalidad(prev => {
      const cur = prev[dimId] || []
      const has = cur.includes(optId)
      return { ...prev, [dimId]: has ? cur.filter(x => x !== optId) : [...cur, optId] }
    })
  }
  const setRadio = (dimId, optId) => {
    setCalidad(prev => ({ ...prev, [dimId]: prev[dimId]?.[0] === optId ? [] : [optId] }))
  }
  const toggleAlcance = (optId) => {
    setAlcance(prev => ({ ...prev, [optId]: !prev[optId] }))
  }

  // Cálculo del % total — aplica el `weight` de cada dimensión sobre el score puro
  const result = useMemo(() => {
    let ptsCalidad = 0
    DIMENSIONS.forEach(dim => {
      const selected = calidad[dim.id] || []
      selected.forEach(optId => {
        const opt = dim.options.find(o => o.id === optId)
        if (opt) ptsCalidad += opt.score * dim.weight
      })
    })
    let ptsAlcance = 0
    ALCANCE_MATERIALES.forEach(opt => {
      if (alcance[opt.id]) ptsAlcance += opt.score
    })

    // El Excel oficial calcula el % combinando ambas secciones.
    // Aproximación: % global = (calidad + alcance) / (max_calidad + max_alcance) * 100
    const totalObtenido = ptsCalidad + ptsAlcance
    const totalMax = TOTAL_CALIDAD_MAX + ALCANCE_MAX
    const pct = (totalObtenido / totalMax) * 100

    const points = pctToPoints(pct, isIndustrial)
    return {
      ptsCalidad, maxCalidad: TOTAL_CALIDAD_MAX,
      ptsAlcance, maxAlcance: ALCANCE_MAX,
      totalObtenido, totalMax,
      pct, points,
    }
  }, [calidad, alcance, isIndustrial])

  // Notificar cambios al padre con debounce ligero
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  useEffect(() => {
    if (onChangeRef.current) {
      onChangeRef.current({
        calidad, alcance, isIndustrial,
        ptsCalidad: result.ptsCalidad, ptsAlcance: result.ptsAlcance,
        pct: Math.round(result.pct * 10) / 10,
        ptsBREEAM: result.points.base,
        ptsEjemplarINO: result.points.ejemplar,
      })
    }
  }, [calidad, alcance, isIndustrial, result.ptsCalidad, result.ptsAlcance, result.pct, result.points.base, result.points.ejemplar])

  const renderOption = (dim, opt) => {
    const selected = (calidad[dim.id] || []).includes(opt.id)
    return (
      <div
        key={opt.id}
        onClick={() => dim.radio ? setRadio(dim.id, opt.id) : toggleMulti(dim.id, opt.id)}
        style={{
          ...ROW_BTN,
          background: selected ? `${C.green}15` : 'transparent',
          border: `1px solid ${selected ? C.green : 'transparent'}`,
        }}
      >
        {selected ? <CheckSquare size={16} color={C.green} weight="fill" /> : <Square size={16} color={C.muted} />}
        <span style={{ flex: 1 }}>
          {opt.label}
          {opt.mandatory && <span style={{ color: C.red, marginLeft: '0.3rem', fontSize: '0.7rem' }}>(M)</span>}
        </span>
        <span style={{ fontSize: '0.72rem', color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>+{opt.score}</span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ ...CARD, background: '#FAF7F3', borderLeft: `3px solid ${C.accent}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: C.dark }}>Calculadora BREEAM ES MAT 1 oficial</div>
            <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '0.25rem' }}>
              Replica el Excel ITG — mide RIGOR del ACV en 6 dimensiones + alcance materiales (Tabla 33 manual)
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: C.dark }}>
            <input type="checkbox" checked={isIndustrial} onChange={e => setIsIndustrial(e.target.checked)} />
            Edificio industrial (Tabla 33 columna izquierda)
          </label>
        </div>
      </div>

      {/* 6 Dimensiones de calidad */}
      {DIMENSIONS.map(dim => {
        const selected = calidad[dim.id] || []
        const ptsDim = selected.reduce((s, optId) => s + (dim.options.find(o => o.id === optId)?.score || 0) * dim.weight, 0)
        return (
          <div key={dim.id} style={CARD}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '700', color: C.dark }}>{dim.label}</div>
                <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.2rem' }}>
                  {dim.description} · Peso ×{dim.weight} · {dim.radio ? 'Elige UNA opción' : 'Marca las aplicables'}
                </div>
              </div>
              <div style={{ background: ptsDim > 0 ? C.green : C.cream, color: ptsDim > 0 ? '#fff' : C.muted, padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', fontFamily: "'IBM Plex Mono', monospace" }}>
                {ptsDim} / {dim.maxPts}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              {dim.options.map(opt => renderOption(dim, opt))}
            </div>
          </div>
        )
      })}

      {/* Alcance de materiales (multi-select largo) */}
      <div style={CARD}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '700', color: C.dark }}>7. Alcance de materiales evaluado</div>
            <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.2rem' }}>Marca cada elemento del edificio incluido en el ACV · (M) = mandatorios según manual</div>
          </div>
          <div style={{ background: result.ptsAlcance > 0 ? C.green : C.cream, color: result.ptsAlcance > 0 ? '#fff' : C.muted, padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', fontFamily: "'IBM Plex Mono', monospace" }}>
            {result.ptsAlcance} / {ALCANCE_MAX}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.2rem' }}>
          {ALCANCE_MATERIALES.map(opt => {
            const sel = !!alcance[opt.id]
            return (
              <div
                key={opt.id}
                onClick={() => toggleAlcance(opt.id)}
                style={{
                  ...ROW_BTN,
                  background: sel ? `${C.green}15` : 'transparent',
                  border: `1px solid ${sel ? C.green : 'transparent'}`,
                }}
              >
                {sel ? <CheckSquare size={14} color={C.green} weight="fill" /> : <Square size={14} color={C.muted} />}
                <span style={{ flex: 1, fontSize: '0.74rem' }}>
                  {opt.label}
                  {opt.mandatory && <span style={{ color: C.red, marginLeft: '0.2rem', fontSize: '0.65rem' }}>(M)</span>}
                </span>
                <span style={{ fontSize: '0.7rem', color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>+{opt.score}</span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Resultado final */}
      <div style={{ ...CARD, background: '#FAF7F3', borderLeft: `4px solid ${C.green}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{ fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Resultado calculadora oficial</div>
            <div style={{ fontSize: '1.6rem', fontWeight: '700', color: C.dark, fontFamily: "'IBM Plex Mono', monospace" }}>
              {result.pct.toFixed(1)}%
            </div>
            <div style={{ fontSize: '0.75rem', color: C.muted }}>
              Calidad ACV: {result.ptsCalidad}/{result.maxCalidad} · Alcance materiales: {result.ptsAlcance}/{result.maxAlcance}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.7rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Puntos BREEAM (Tabla 33)</div>
            <div style={{ fontSize: '1.8rem', fontWeight: '700', color: C.green, fontFamily: "'IBM Plex Mono', monospace" }}>
              {result.points.base}
              {result.points.ejemplar > 0 && <span style={{ fontSize: '1rem', color: C.amber, marginLeft: '0.4rem' }}>+ {result.points.ejemplar} INO</span>}
            </div>
            <div style={{ fontSize: '0.72rem', color: C.muted }}>
              {result.points.ejemplar > 0
                ? '★ Nivel Ejemplar — punto extraordinario al INO 1'
                : result.points.base >= 6
                ? 'Máximo MAT 1 alcanzado'
                : `Para máximo: necesitas ${isIndustrial ? '82,5%' : '82,5%'}. Para Ejemplar: ≥85%.`}
            </div>
          </div>
        </div>
        {/* Tabla 33 visual */}
        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
          {[10, 35, 62.5, 75, 80, 82.5, 85].map(threshold => {
            const reached = result.pct >= threshold
            return (
              <div key={threshold} style={{
                padding: '0.2rem 0.45rem', borderRadius: '3px', fontSize: '0.7rem',
                background: reached ? C.green : C.cream,
                color: reached ? '#fff' : C.muted,
                fontFamily: "'IBM Plex Mono', monospace",
              }}>
                ≥{threshold}%
              </div>
            )
          })}
        </div>
      </div>

      {/* Nota explicativa */}
      <div style={{ ...CARD, background: '#FDF8E8', borderLeft: `3px solid ${C.amber}`, fontSize: '0.78rem', color: C.muted, lineHeight: '1.5' }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
          <Info size={14} color={C.amber} weight="fill" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong style={{ color: C.dark }}>BREEAM ES NC 2015 — NA3 del manual:</strong> MAT 1 mide el <strong>RIGOR</strong> del ACV (calidad de la herramienta + ámbito de elementos),
            <strong> NO el GWP absoluto por m²</strong>. Por eso esta calculadora oficial puntúa por dimensiones independientes del valor final
            de huella de carbono. El indicador de "% reducción CO₂ vs benchmark" (que se ve más abajo en la pestaña OneClick) es
            informativo y NO determina los puntos BREEAM directamente.
          </div>
        </div>
      </div>
    </div>
  )
}
